<template>
    <div class="h-full min-h-screen flex flex-col" key="content" v-if="contentLoaded">
        <!-- Banners -->
        <status-banner />
        <announcement-banner />
        <impersonation-banner />

        <div class="flex-1 relative flex">
            <template v-if="navIsEnabled">
                <expandable-main-nav-container />
            </template>

            <main
                class="flex-1 flex flex-col w-0 | transition-spacing duration-300"
                :class="{
                    'max-h-screen': $route.meta.fixedContent,
                    'ml-20': navIsEnabled && !isTouchDevice,
                }"
            >
                <main-header v-if="includeInLayout('header')" />
                <div
                    class="flex-1 flex | transition-spacing duration-300"
                    :class="
                        sidebar.opened && sidebar.push ? 'md:mr-80 xl:mr-96 2xl:mr-128' : 'mr-0'
                    "
                >
                    <div
                        id="portal-left-sidebar"
                        :style="leftSidebarStyle"
                        class="sticky left-0 top-0 z-30"
                    />
                    <div
                        class="relative flex-1 max-w-full flex flex-col print:p-0"
                        :class="includeInLayout('contentPadding') ? 'p-3 md:p-6' : ''"
                        v-show="!contextLoading"
                    >
                        <div
                            :class="slideOverContainerClass"
                            :style="slideOverContainerStyle"
                            ref="slideOverContainer"
                        >
                            <div
                                class="absolute right-0 top-0 flex flex-col items-end justify-end gap-6"
                                :style="slideOverPanelContainerStyle"
                            >
                                <slide-over type="addLead" />
                                <slide-over type="interAccountLeads" />
                                <slide-over type="smsConversation" />
                            </div>
                        </div>
                        <content-header v-if="includeInLayout('contentHeader')" />
                        <!-- Vue 3 renders :prop="false" as the string "false" on DOM attributes.
                             Coercing to undefined omits the attribute entirely when the sidebar is closed. -->
                        <router-view
                            :right-sidebar-opened="sidebar.opened || undefined"
                            class="relative flex-1"
                        />
                        <div id="portal-content-footer" />

                        <div class="sticky inset-x-0 bottom-6 flex z-20 pointer-events-none">
                            <div id="portal-anchor-bottom-left" class="pointer-events-auto" />
                            <div
                                id="portal-anchor-bottom-right"
                                class="ml-auto pointer-events-auto"
                            />
                        </div>
                    </div>
                </div>
            </main>
        </div>

        <listen-to-broadcasting />

        <screen-pop v-if="!$browser.isMobileWebView()" />
        <screen-pop-video-conference v-if="!$browser.isMobileWebView()" />

        <sms-lead />
        <confidentiality-agreement />
        <modal-event />
        <call-summary />
        <activix-confirm-modal
            portal="modal-event-sub"
            :content="$t('modal.createAppointmentConfirmMessage')"
            :title="$t('modal.createAppointment')"
            @approve="openAppointmentModal"
            v-model:opened="createAppointmentModal.opened"
        />

        <support-modal v-model:opened="supportModalOpened" />
    </div>
</template>

<script>
    import { useHead } from '@unhead/vue';
    import { mapActions, mapState, storeToRefs } from 'pinia';
    import * as Sentry from '@sentry/vue';
    import { isTouchDevice } from '@/utils/device.js';
    import IntercomClient from '@/plugins/intercom.js';
    import { measureStartupPhase } from '@/telemetry/startupTiming.ts';
    import TaskEventType from '@/entities/TaskEventType.js';
    import SupportUrl from '@/mixins/SupportUrl.js';

    // Hooks
    import { useEventBus } from '@/hooks/useEventBus.ts';

    // Components
    import ActivixConfirmModal from '@/components/elements/ActivixConfirmModal.vue';
    import AnnouncementBanner from '@/components/AnnouncementBanner.vue';
    import CallSummary from '@/components/modals/CallSummary.vue';
    import ConfidentialityAgreement from '@/components/modals/ConfidentialityAgreement.vue';
    import ContentHeader from '@/components/ContentHeader.vue';
    import ExpandableMainNavContainer from '@/components/ExpandableMainNavContainer.vue';
    import ListenToBroadcasting from '@/components/utils/ListenToBroadcasting.js';
    import MainHeader from '@/components/MainHeader.vue';
    import ModalEvent from '@/components/modals/ModalEvent.vue';
    import ScreenPop from '@/components/modals/ScreenPop.vue';
    import ScreenPopVideoConference from '@/components/modals/ScreenPopVideoConference.vue';
    import SmsLead from '@/components/modals/SmsLead.vue';
    import StatusBanner from '@/components/StatusBanner.vue';
    import ImpersonationBanner from '@/components/ImpersonationBanner.vue';
    import SlideOver from '@/components/SlideOver.vue';

    // Pinia
    import { useContextStore } from '@/store/modules/context/store.ts';
    import { useLayoutStore } from '@/store/modules/layout/store.js';
    import { useGlobalStore } from '@/store/store.ts';
    import { useAccountStore } from '@/store/modules/account/store.js';
    import { useUserStore } from '@/store/modules/user/store.js';

    const { on, off, emit } = useEventBus();

    export default {
        emits: ['update:showLoading'],

        name: 'DefaultLayout',

        components: {
            ActivixConfirmModal,
            AnnouncementBanner,
            CallSummary,
            ConfidentialityAgreement,
            ContentHeader,
            ExpandableMainNavContainer,
            ListenToBroadcasting,
            MainHeader,
            ModalEvent,
            ScreenPop,
            ScreenPopVideoConference,
            SlideOver,
            SmsLead,
            StatusBanner,
            ImpersonationBanner,
        },

        mixins: [SupportUrl],

        setup() {
            const { pageTitle } = storeToRefs(useGlobalStore());
            useHead({ title: pageTitle });
        },

        props: {
            showLoading: {
                type: Boolean,
                require: true,
            },
            contextLoading: {
                type: Boolean,
                default: true,
            },
        },

        data() {
            return {
                loaded: false,
                slideOverAvailableHeight: null,
                slideOverScrollY: 0,
                slideOverTopGap: 0,
                slideOverViewportWidth: globalThis.innerWidth,
                createAppointmentModal: {
                    opened: false,
                    payload: {},
                },
                sidebar: {
                    opened: false,
                    push: false,
                },
                isTouchDevice: isTouchDevice(),
            };
        },

        computed: {
            ...mapState(useLayoutStore, ['navExpanded', 'bodySpacingTop', 'headerHeight']),
            ...mapState(useContextStore, {
                contextUser: 'user',
                contextGroup: 'group',
            }),

            parentAuthUser() {
                return useUserStore().parentAuthUser;
            },

            authUser() {
                return useUserStore().authUser;
            },

            contextAccount() {
                return useAccountStore().contextAccount;
            },

            navIsEnabled() {
                return this.includeInLayout('nav') && !this.$browser.isMobileWebView();
            },

            contentLoaded() {
                return (
                    this.loaded &&
                    this.$route.meta.auth !== false &&
                    !!this.authUser?.id &&
                    (!!this.contextAccount?.id || this.$route.name === 'accounts.store')
                );
            },

            contextQuery() {
                const accountId =
                    this.canSaveInUrl('accountId') && this.contextAccount
                        ? this.contextAccount.id
                        : null;
                const groupId =
                    this.canSaveInUrl('groupId') && this.contextGroup ? this.contextGroup.id : null;
                const userId =
                    this.canSaveInUrl('userId') && this.contextUser ? this.contextUser.id : null;

                return {
                    accountId,
                    groupId,
                    userId,
                };
            },

            leftSidebarStyle() {
                const top = this.headerHeight + this.bodySpacingTop;

                return {
                    top: `${top}px`,
                    height: `calc(100vh - ${top}px)`,
                };
            },

            slideOverOffsetTop() {
                return this.bodySpacingTop + this.headerHeight + this.slideOverTopGap;
            },

            slideOverContainerClass() {
                if (this.slideOverViewportWidth >= 768 || this.slideOverScrollY === 0) {
                    return 'sticky z-30 h-0 pointer-events-none';
                }

                return 'fixed inset-x-3 z-30 h-0 pointer-events-none';
            },

            slideOverContainerStyle() {
                return {
                    top: `${this.slideOverOffsetTop}px`,
                    '--slide-over-available-height':
                        typeof this.slideOverAvailableHeight === 'number'
                            ? `${this.slideOverAvailableHeight}px`
                            : `calc(100vh - ${this.slideOverOffsetTop}px)`,
                };
            },

            slideOverPanelContainerStyle() {
                return {
                    height: 'var(--slide-over-available-height, calc(100vh - 7.5rem))',
                };
            },
        },

        watch: {
            '$route.name': {
                async handler(newVal) {
                    this.updateHeadTitle(newVal);
                    this.verifyAccess();
                    IntercomClient.ping();
                    await this.$nextTick();
                    this.updateSlideOverTopGap();
                },
                immediate: true,
            },

            contentLoaded: {
                immediate: true,
                async handler(contentLoaded) {
                    this.$emit('update:showLoading', !contentLoaded);

                    if (contentLoaded) {
                        await this.$nextTick();
                        this.updateSlideOverTopGap();
                        IntercomClient.init(this.authUser);
                    }
                },
            },

            headerHeight() {
                this.updateSlideOverAvailableHeight();
            },

            bodySpacingTop() {
                this.updateSlideOverAvailableHeight();
            },

            contextQuery(newContextQuery) {
                this.updateQuery(newContextQuery);
            },

            'contextAccount.id': {
                immediate: true,
                handler(newAccountId, oldAccountId) {
                    if (!newAccountId || newAccountId == oldAccountId) {
                        return;
                    }

                    this.verifyAccess();

                    this.$broadcasting.accountChanged(newAccountId, oldAccountId);
                },
            },

            'parentAuthUser.confidentiality_agreement'() {
                IntercomClient.init(this.parentAuthUser);
            },
        },

        methods: {
            updateSlideOverViewportState() {
                this.slideOverViewportWidth = globalThis.innerWidth;
                this.slideOverScrollY = Math.max(Math.round(globalThis.scrollY), 0);
                this.updateSlideOverAvailableHeight();
            },

            updateSlideOverAvailableHeight() {
                globalThis.requestAnimationFrame(() => {
                    const slideOverContainer = this.$refs.slideOverContainer;
                    const viewportHeight =
                        globalThis.visualViewport?.height ?? globalThis.innerHeight;

                    if (!(slideOverContainer instanceof HTMLElement)) {
                        return;
                    }

                    this.slideOverAvailableHeight = Math.max(
                        Math.round(viewportHeight - slideOverContainer.getBoundingClientRect().top),
                        0
                    );
                });
            },

            updateSlideOverTopGap() {
                globalThis.requestAnimationFrame(() => {
                    const header = document.querySelector('.main-header');
                    const contentHeader = document.querySelector('#portal-content-header');
                    const contentHeaderTitle =
                        contentHeader instanceof HTMLElement
                            ? [...contentHeader.children].find(
                                  (child) =>
                                      child instanceof HTMLElement &&
                                      getComputedStyle(child).display !== 'none'
                              )
                            : null;

                    if (
                        !(header instanceof HTMLElement) ||
                        !(contentHeaderTitle instanceof HTMLElement)
                    ) {
                        this.slideOverTopGap = 0;
                        this.$nextTick(() => {
                            this.updateSlideOverAvailableHeight();
                        });
                        return;
                    }

                    const gap =
                        contentHeaderTitle.getBoundingClientRect().top -
                        header.getBoundingClientRect().bottom;

                    this.slideOverTopGap = Math.max(Math.round(gap), 0);
                    this.$nextTick(() => {
                        this.updateSlideOverAvailableHeight();
                    });
                });
            },

            updateHeadTitle(routeName) {
                this.setPageTitle(routeName ? this.$t(`titles.${routeName}`) : '');
            },
            ...mapActions(useLayoutStore, ['setBodyMarginTop', 'setBodyPaddingTop']),
            ...mapActions(useContextStore, [
                'setContextDivisionId',
                'setContextTeamId',
                'setContextGroupAction',
                'setContextLeadIdAction',
                'setContextUserAction',
            ]),
            ...mapActions(useGlobalStore, ['setGroups', 'setPageTitle']),

            async loadInitialData() {
                await measureStartupPhase('groups', () => this.fetchGroups());
                this.$api.authUser.triggerScreenPop();
            },

            async fetchGroups() {
                const groups = await this.$api.groups.index();

                this.setGroups(groups);
            },

            openAppointmentModal() {
                emit('open-add-task-event', {
                    leadId: this.createAppointmentModal.payload.leadId,
                    title: this.createAppointmentModal.payload.title,
                    description: this.createAppointmentModal.payload.description,
                    guests: this.createAppointmentModal.payload.guests,
                    priority: this.createAppointmentModal.payload.priority,
                    user: this.createAppointmentModal.payload.user_id,
                    reminders: this.createAppointmentModal.payload.reminders,
                    taskEventType: TaskEventType.APPOINTMENT,
                });
            },

            verifyAccess() {
                if (!this.$route.name || this.$route.name == 'auth.login' || !this.contextAccount) {
                    return;
                }

                if (this.$route.name === 'analytics') {
                    if (
                        !this.parentAuthUser?.hasAccessTo(this.$route.name, this.contextAccount.id)
                    ) {
                        this.$router.replace({ name: 'unauthorized' });
                    }

                    return;
                }

                if (!this.authUser?.hasAccessTo(this.$route.name, this.contextAccount.id)) {
                    this.$router.replace({ name: 'unauthorized' });
                }
            },

            canSaveInUrl(key) {
                const saveInUrl = this.$route.meta.saveInUrl || [];

                return saveInUrl.includes(key);
            },

            openCreateAppointmentModal(payload) {
                this.createAppointmentModal.opened = true;
                this.createAppointmentModal.payload = payload;
            },

            async init() {
                try {
                    await Promise.all([this.loadInitialData(), this.$auth.waitForUser(true)]);
                } catch (e) {
                    Sentry.captureException(e);
                } finally {
                    this.loaded = true;
                }
            },

            getRedirect() {
                return this.getReferer();
            },

            getReferer() {
                const referer = this.$ls.get('referer');

                this.$ls.remove('referer');

                return referer;
            },

            onBodyMutation(mutations) {
                mutations.forEach((mutation) => {
                    this.setBodyMarginTop(mutation.target.style.marginTop);
                    this.setBodyPaddingTop(mutation.target.style.paddingTop);
                });
            },
        },

        mounted() {
            this.updateHeadTitle(this.$route.name);
            this.updateSlideOverTopGap();
            this.updateSlideOverViewportState();
            window.addEventListener('scroll', this.updateSlideOverViewportState, { passive: true });
            window.addEventListener('resize', this.updateSlideOverViewportState);
            window.visualViewport?.addEventListener('resize', this.updateSlideOverViewportState);
        },

        created() {
            const redirect = this.getRedirect();

            if (redirect === 'support') {
                this.openSupport(false);
            } else {
                this.init();
            }

            this.bodyObserver = new MutationObserver(this.onBodyMutation);
            this.bodyObserver.observe(document.body, {
                attributeFilter: ['style'],
            });

            on('open-modal-event-appointment', this.openCreateAppointmentModal);
            on('load-initial-data', this.loadInitialData);
        },

        beforeUnmount() {
            off('open-modal-event-appointment', this.openCreateAppointmentModal);
            off('load-initial-data', this.loadInitialData);

            window.removeEventListener('scroll', this.updateSlideOverViewportState);
            window.removeEventListener('resize', this.updateSlideOverViewportState);
            window.visualViewport?.removeEventListener('resize', this.updateSlideOverViewportState);

            this.bodyObserver.disconnect();
            this.bodyObserver = null;
        },
    };
</script>
